import type { TFunction } from "i18next";
import { calFileSizeV2 } from "@whatslly/commons/utils";
import { DLPAction } from "@whatslly/types/dlp/DLPPolicy";
import type { DLPRule } from "@whatslly/types/dlp/DLPRule";
import type {
  PolicyBuilderContactScopeType,
  PolicyBuilderRuleGroup,
  PolicyBuilderSenderScopeType,
  PolicyBuilderState,
} from "@whatslly/types/policy/PolicyBuilder";
import { MIME_TYPE_OPTIONS, type PolicyCondition } from "@whatslly/types/policy/conditions";
import { Direction, Operator, OutboundMessageType } from "@whatslly/types/policy/enums";
import type { SelectOption } from "@whatslly/types/UI-V2/BaseElements/SelectType";

const DEFAULT_PRIORITY = 1;
const DEFAULT_SEVERITY = 4;

export const getSeverityOptions = (t: TFunction): SelectOption<{ value: number }>[] => [
  { label: t("dlp_page.low"), value: 4 },
  { label: t("dlp_page.medium"), value: 3 },
  { label: t("dlp_page.high"), value: 2 },
  { label: t("dlp_page.critical"), value: 1 },
];

export const getOutboundTypeOptions = (t: TFunction): SelectOption<{ value: OutboundMessageType }>[] => [
  { label: t("dlp_page.outbound_message_types.text"), value: OutboundMessageType.TEXT },
  { label: t("dlp_page.outbound_message_types.event"), value: OutboundMessageType.EVENT },
  { label: t("dlp_page.outbound_message_types.order"), value: OutboundMessageType.ORDER },
  { label: t("dlp_page.outbound_message_types.poll"), value: OutboundMessageType.POLL },
  { label: t("dlp_page.outbound_message_types.contact"), value: OutboundMessageType.CONTACT },
];

export const getFileSizeOperators = (t: TFunction): SelectOption<{ value: Operator }>[] => [
  { label: t("dlp_page.operators.gt"), value: Operator.GT },
  { label: t("dlp_page.operators.lt"), value: Operator.LT },
  { label: t("dlp_page.operators.gte"), value: Operator.GTE },
  { label: t("dlp_page.operators.lte"), value: Operator.LTE },
];

export const getMimeTypeLabel = (mimeType: string) => MIME_TYPE_OPTIONS.find((option) => option.value === mimeType)?.label ?? mimeType;

export const getFileSizeOperatorSymbol = (operator: Operator) =>
  ({
    [Operator.GT]: ">",
    [Operator.LT]: "<",
    [Operator.GTE]: "≥",
    [Operator.LTE]: "≤",
  })[operator];

export const getFileSizePillSummary = (operator: Operator, fileSize: number) =>
  `${getFileSizeOperatorSymbol(operator)} ${calFileSizeV2({ size: fileSize })}`;

export const createRuleGroup = (ruleIds: string[] = []): PolicyBuilderRuleGroup => ({
  id: crypto.randomUUID(),
  ruleIds,
});

const getRuleGroupIds = (condition: Extract<PolicyCondition, { type: "rule" }>) => {
  return Array.isArray(condition.id) ? condition.id : [condition.id];
};

export const getScopeFromConditions = (conditions: PolicyCondition[]) => {
  const userCondition = conditions.find((condition): condition is Extract<PolicyCondition, { type: "user" }> => condition.type === "user");
  const groupCondition = conditions.find((condition): condition is Extract<PolicyCondition, { type: "group" }> => condition.type === "group");
  const internalUsersCondition = conditions.find(
    (condition): condition is Extract<PolicyCondition, { type: "internal_users" }> => condition.type === "internal_users"
  );

  let senderScopeType: PolicyBuilderSenderScopeType = "allUsers";
  if (userCondition) {
    senderScopeType = "specificUsers";
  } else if (groupCondition) {
    senderScopeType = "specificGroups";
  }

  const contactScopeType: PolicyBuilderContactScopeType = internalUsersCondition
    ? internalUsersCondition.include
      ? "internalUsers"
      : "externalContacts"
    : "anyContact";

  return {
    senderScopeType,
    contactScopeType,
    selectedUserIds: userCondition?.userId ?? [],
    selectedGroupIds: groupCondition?.groupId ?? [],
  };
};

export const getConditionSections = (conditions: PolicyCondition[]) => {
  const directionCondition = conditions.find(
    (condition): condition is Extract<PolicyCondition, { type: "direction" }> => condition.type === "direction"
  );
  const mimeTypeCondition = conditions.find(
    (condition): condition is Extract<PolicyCondition, { type: "mimeType" }> => condition.type === "mimeType"
  );
  const fileSizeCondition = conditions.find(
    (condition): condition is Extract<PolicyCondition, { type: "fileSize" }> => condition.type === "fileSize"
  );
  const ruleGroups = conditions
    .filter((condition): condition is Extract<PolicyCondition, { type: "rule" }> => condition.type === "rule")
    .map((condition) => createRuleGroup(getRuleGroupIds(condition)));

  return {
    direction: directionCondition?.direction,
    outboundMessageTypes:
      directionCondition?.direction === Direction.OUTBOUND
        ? directionCondition.msgType?.length
          ? directionCondition.msgType
          : [OutboundMessageType.TEXT]
        : [],
    includeTemplates: conditions.some((condition) => condition.type === "template"),
    includeDevtools: conditions.some((condition) => condition.type === "devtools"),
    mimeTypes: mimeTypeCondition?.mimeType ?? [],
    fileSizeEnabled: Boolean(fileSizeCondition),
    fileSize: fileSizeCondition?.size ?? fileSizeCondition?.maxSize ?? 1000000,
    fileSizeOperator: fileSizeCondition?.operator ?? Operator.GT,
    ruleGroups,
  };
};

export const createDefaultBuilderState = (): PolicyBuilderState => ({
  enabled: true,
  name: "",
  description: "",
  priority: DEFAULT_PRIORITY,
  action: DLPAction.ALLOW,
  shouldNotifyByEmail: false,
  shouldOpenIncident: false,
  shouldAttachMsgContent: false,
  severity: DEFAULT_SEVERITY,
  emails: [],
  senderScopeType: "allUsers",
  contactScopeType: "anyContact",
  selectedUserIds: [],
  selectedGroupIds: [],
  direction: null,
  outboundMessageTypes: [OutboundMessageType.TEXT],
  includeTemplates: false,
  includeDevtools: false,
  mimeTypes: [],
  fileSizeEnabled: false,
  fileSize: 1000000,
  fileSizeOperator: Operator.GT,
  ruleGroups: [],
  customAlertMessage: "",
});

export const buildScopeConditions = (state: PolicyBuilderState): PolicyCondition[] => {
  const conditions: PolicyCondition[] = [];

  switch (state.senderScopeType) {
    case "specificUsers":
      if (state.selectedUserIds.length > 0) {
        conditions.push({ type: "user", userId: state.selectedUserIds });
      }
      break;
    case "specificGroups":
      if (state.selectedGroupIds.length > 0) {
        conditions.push({ type: "group", groupId: state.selectedGroupIds });
      }
      break;
  }

  switch (state.contactScopeType) {
    case "internalUsers":
      conditions.push({ type: "internal_users", include: true });
      break;
    case "externalContacts":
      conditions.push({ type: "internal_users", include: false });
      break;
  }

  return conditions;
};

export const buildConditionSections = (state: PolicyBuilderState): PolicyCondition[] => {
  const conditions: PolicyCondition[] = [];

  if (state.direction) {
    if (state.direction === Direction.OUTBOUND) {
      conditions.push({
        type: "direction",
        direction: Direction.OUTBOUND,
        msgType: state.outboundMessageTypes.length > 0 ? state.outboundMessageTypes : [OutboundMessageType.TEXT],
      });
    } else {
      conditions.push({
        type: "direction",
        direction: state.direction,
      });
    }
  }

  if (state.includeTemplates) {
    conditions.push({ type: "template" });
  }

  if (state.includeDevtools) {
    conditions.push({ type: "devtools" });
  }

  if (state.mimeTypes.length > 0) {
    conditions.push({ type: "mimeType", mimeType: state.mimeTypes });
  }

  if (state.fileSizeEnabled) {
    conditions.push({
      type: "fileSize",
      size: state.fileSize,
      operator: state.fileSizeOperator,
    });
  }

  conditions.push(
    ...state.ruleGroups
      .filter((ruleGroup) => ruleGroup.ruleIds.length > 0)
      .map((ruleGroup): PolicyCondition => ({
        type: "rule",
        id: ruleGroup.ruleIds,
      }))
  );

  return conditions;
};

export const getConditionItems = (
  builderState: PolicyBuilderState,
  rules: DLPRule[],
  t: TFunction,
  fileSizeOperators: SelectOption<{ value: Operator }>[],
  outboundTypeOptions: SelectOption<{ value: OutboundMessageType }>[]
): { label: string; value: string }[] => {
  const items: { label: string; value: string }[] = [];

  if (builderState.direction === undefined) {
    items.push({ label: t("dlp_page.builder.message_type.title"), value: t("dlp_page.conditions.direction_all") });
  } else if (builderState.direction === Direction.OUTBOUND) {
    const outboundTypes = outboundTypeOptions
      .filter((option) => builderState.outboundMessageTypes.includes(option.value))
      .map((option) => option.label);
    items.push({ label: t("dlp_page.builder.message_type.title"), value: t("dlp_page.conditions.direction_OUTBOUND") });
    items.push({ label: t("dlp_page.builder.message_type.outbound_selector"), value: outboundTypes.join(", ") });
  } else if (builderState.direction) {
    items.push({ label: t("dlp_page.builder.message_type.title"), value: t(`dlp_page.conditions.direction_${builderState.direction}`) });
  }

  if (builderState.mimeTypes.length > 0) {
    items.push({ label: t("dlp_page.conditions.mime_type"), value: builderState.mimeTypes.map(getMimeTypeLabel).join(", ") });
  }

  if (builderState.fileSizeEnabled) {
    const operatorLabel = fileSizeOperators.find((option) => option.value === builderState.fileSizeOperator)?.label ?? builderState.fileSizeOperator;
    items.push({
      label: t("dlp_page.conditions.file_size"),
      value: t("dlp_page.builder.summary.file_size", { operator: operatorLabel, size: calFileSizeV2({ size: builderState.fileSize }) }),
    });
  }

  builderState.ruleGroups
    .filter((group) => group.ruleIds.length > 0)
    .forEach((group, index) => {
      const groupRules = group.ruleIds.map((ruleId) => rules.find((rule) => rule.id === ruleId)?.name ?? ruleId).join(" OR ");
      items.push({ label: t("dlp_page.builder.rules.group_title", { number: index + 1 }), value: groupRules });
    });

  if (builderState.includeTemplates) {
    items.push({ label: t("dlp_page.conditions.template"), value: t("yes") });
  }

  if (builderState.includeDevtools) {
    items.push({ label: t("dlp_page.conditions.devtools"), value: t("yes") });
  }

  return items;
};

export const getConditionSummary = (
  builderState: PolicyBuilderState,
  rules: DLPRule[],
  t: TFunction,
  fileSizeOperators: SelectOption<{ value: Operator }>[],
  outboundTypeOptions: SelectOption<{ value: OutboundMessageType }>[]
): string => {
  const segments: string[] = [];

  if (builderState.direction === undefined) {
    segments.push(t("dlp_page.conditions.direction_all"));
  } else if (builderState.direction === Direction.OUTBOUND) {
    const outboundTypes = outboundTypeOptions
      .filter((option) => builderState.outboundMessageTypes.includes(option.value))
      .map((option) => option.label);
    segments.push(
      outboundTypes.length > 0
        ? t("dlp_page.builder.summary.outbound_types", { types: outboundTypes.join(", ") })
        : t("dlp_page.conditions.direction_OUTBOUND")
    );
  } else if (builderState.direction === Direction.DOWNLOAD) {
    segments.push(t("dlp_page.conditions.direction_DOWNLOAD"));
  } else if (builderState.direction === Direction.UPLOAD) {
    segments.push(t("dlp_page.conditions.direction_UPLOAD"));
  } else if (builderState.direction === Direction.INBOUND) {
    segments.push(t("dlp_page.conditions.direction_INBOUND"));
  }

  if (builderState.includeTemplates) {
    segments.push(t("dlp_page.conditions.template"));
  }
  if (builderState.includeDevtools) {
    segments.push(t("dlp_page.conditions.devtools"));
  }
  if (builderState.mimeTypes.length > 0) {
    segments.push(t("dlp_page.builder.summary.file_types", { types: builderState.mimeTypes.map(getMimeTypeLabel).join(", ") }));
  }
  if (builderState.fileSizeEnabled) {
    const operatorLabel = fileSizeOperators.find((option) => option.value === builderState.fileSizeOperator)?.label ?? builderState.fileSizeOperator;
    segments.push(t("dlp_page.builder.summary.file_size", { operator: operatorLabel, size: calFileSizeV2({ size: builderState.fileSize }) }));
  }
  if (builderState.ruleGroups.length > 0) {
    const ruleGroups = builderState.ruleGroups
      .filter((group) => group.ruleIds.length > 0)
      .map((group) => group.ruleIds.map((ruleId) => rules.find((rule) => rule.id === ruleId)?.name ?? ruleId).join(" OR "));
    if (ruleGroups.length > 0) {
      segments.push(ruleGroups.join(` ${t("dlp_page.conditions.and_between_conditions")} `));
    }
  }

  return segments.length > 0 ? segments.join(" • ") : t("dlp_page.builder.summary.no_conditions");
};
